import { useId, useRef, useState, type ReactNode } from 'react';
import { Info } from 'lucide-react';
import { bandByKey, FOURD_STATUS, SEVERITY, type BandKey } from '../theme/bands';
import type { FourDStatus, Severity } from '../types';
import { VTile } from './VMark';

/**
 * Full-screen loading veil — the single, consistent "page is loading" treatment across every
 * surface. A dimmed, blurred backdrop over the whole app with the branded V-mark and a label,
 * so a page never renders the wrong content while its data is still in flight (e.g. the
 * Dashboard flashing the get-started hero on login before the report arrives).
 *
 * Blocks interaction by design while shown. `role="status"` + `aria-live` announces it to
 * assistive tech; the pulse ring honours prefers-reduced-motion via the global CSS reset.
 * Pass `show={false}` to render nothing so callers can mount it unconditionally.
 */
export function PageLoader({ label = 'Loading…', show = true }: { label?: string; show?: boolean }) {
  if (!show) return null;
  return (
    <div
      className="animate-fade fixed inset-0 z-[60] flex flex-col items-center justify-center gap-5 print:hidden"
      style={{ background: 'rgba(11,11,15,0.55)', backdropFilter: 'blur(3px)' }}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <span className="relative inline-flex">
        <VTile size={60} className="animate-breathe" />
        <span className="pointer-events-none absolute inset-0 animate-pulseRing rounded-ctl ring-2 ring-green" />
      </span>
      <span className="text-[14px] font-semibold tracking-[0.02em] text-white">{label}</span>
    </div>
  );
}

export function Eyebrow({ children, dark = false }: { children: ReactNode; dark?: boolean }) {
  return (
    <p className="eyebrow m-0" style={dark ? { color: '#75757F' } : undefined}>
      {children}
    </p>
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <section className={`rounded-card border border-panel bg-white p-6 ${className}`}>{children}</section>
  );
}

type BtnVariant = 'primary' | 'ghost' | 'ghostDark';
export function Button({
  children,
  onClick,
  variant = 'primary',
  glow = false,
  full = false,
  type = 'button',
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: BtnVariant;
  glow?: boolean;
  full?: boolean;
  type?: 'button' | 'submit';
}) {
  const base =
    'inline-flex items-center justify-center gap-2 rounded-ctl px-5 py-3 text-[15px] font-semibold transition-colors duration-150';
  const styles: Record<BtnVariant, string> = {
    primary: 'bg-green text-black hover:bg-green-hover',
    ghost: 'border border-code text-code hover:bg-code hover:text-white bg-transparent',
    ghostDark: 'border border-dark-border2 text-white hover:bg-white hover:text-black bg-transparent',
  };
  return (
    <button
      type={type}
      onClick={onClick}
      className={`${base} ${styles[variant]} ${full ? 'w-full' : ''}`}
      style={glow ? { boxShadow: '0 8px 28px rgba(0,215,0,0.30)' } : undefined}
    >
      {children}
    </button>
  );
}

/** Band identity — label ALWAYS paired with the swatch (colour is never the only cue). */
export function BandChip({ band, label }: { band: BandKey; label?: string }) {
  const def = bandByKey(band);
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11.5px] font-bold"
      style={{ background: def.fill, color: def.text }}
    >
      <span
        className="inline-block h-2 w-2 rounded-full"
        style={{ background: def.text, opacity: 0.85 }}
        aria-hidden="true"
      />
      {label ?? def.label}
    </span>
  );
}

export function FourDChip({ status }: { status: FourDStatus }) {
  const s = FOURD_STATUS[status];
  return (
    <span
      className="inline-flex items-center rounded-full px-3 py-1 text-[11.5px] font-bold"
      style={{ background: s.fill, color: s.text }}
    >
      {s.label}
    </span>
  );
}

/**
 * Small accessible definition tooltip. An info icon button reveals a short definition on hover
 * and on keyboard focus, and hides it on blur or Escape. The button is labelled for screen
 * readers and the panel is wired via aria-describedby so the text is read out on focus.
 *
 * The panel is absolutely positioned within a relatively-positioned wrapper, with a high
 * z-index so it sits above sticky page elements (e.g. the report radar). `label` names the
 * term the tip explains (used in the button's accessible name).
 */
export function InfoTip({ label, text }: { label: string; text: string }) {
  const [open, setOpen] = useState(false);
  const tipId = useId();
  const wrapRef = useRef<HTMLSpanElement>(null);

  // Close when focus leaves the whole control (button + panel).
  const onBlur = (e: React.FocusEvent<HTMLSpanElement>) => {
    if (!wrapRef.current?.contains(e.relatedTarget as Node | null)) setOpen(false);
  };

  return (
    <span
      ref={wrapRef}
      className="relative inline-flex print:hidden"
      style={{ verticalAlign: 'middle' }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onBlur={onBlur}
    >
      <button
        type="button"
        aria-label={`What is ${label}?`}
        aria-expanded={open}
        aria-describedby={open ? tipId : undefined}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
        className="inline-flex items-center justify-center rounded-full text-[#9A9AA4] transition-colors hover:text-[#333544] focus-visible:text-[#333544] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2438E6]"
        style={{ width: 18, height: 18, cursor: 'help', background: 'transparent', border: 'none', padding: 0 }}
      >
        <Info size={14} aria-hidden="true" />
      </button>
      {open && (
        <span
          role="tooltip"
          id={tipId}
          className="absolute left-1/2 z-[90] block w-max max-w-[260px] -translate-x-1/2 rounded-lg px-3 py-2 text-left text-[12.5px] font-normal leading-snug shadow-lg"
          style={{ top: 'calc(100% + 8px)', background: '#0B0B0F', color: '#F4F4F6', whiteSpace: 'normal' }}
        >
          {text}
        </span>
      )}
    </span>
  );
}

export function SeverityChip({ severity }: { severity: Severity }) {
  const s = SEVERITY[severity];
  return (
    <span
      className="inline-flex items-center rounded-full px-3 py-1 text-[11px] font-bold"
      style={{ background: s.fill, color: s.text }}
    >
      {s.label}
    </span>
  );
}
